export const PAPI_PACKING_SLIP_DOC_TYPE = "packingSlip";
export const PAPI_DOCUMENT_NOT_INCLUDED_LABEL = "Not Included";
export const PAPI_PACKING_SLIP_NOT_INCLUDED_LABEL = PAPI_DOCUMENT_NOT_INCLUDED_LABEL;
export const PAPI_PACKING_SLIP_OTHER_SIZE_LABEL = "Other";
export const PAPI_UCC128_DOC_TYPE = "ucc128";
export const PAPI_UCC128_NOT_SPECIFIED_LABEL = PAPI_DOCUMENT_NOT_INCLUDED_LABEL;

export const DOC_ENGINE_PAPI = "papi";
/** Pack & Ship print engine for the legacy runner. Never a dropdown value prefix. */
export const DOC_ENGINE_LEGACY = "ssrs";
const DOC_VALUE_SEPARATOR = ":";

/** Formats the PAPI engine can render, for any docType. */
export const PAPI_FORMAT_CAPABILITIES = ["pdf", "zpl"];

export const SSRS_OUTPUT_TYPES = {
    pdf: "application/pdf",
    csv: "text/csv;header=absent;separator=comma;encoding=utf-8;excelmode=true",
    excel: "application/vnd.ms-excel",
    word: "application/msword;autofit=false;fixedpagewidth=false",
    xml: "application/xml",
    zpl: "text/plain",
};

/**
 * Canonical dropdown value: "<engine>:<rawId>".
 * Blank/null rawId returns null so the empty ("Not Included") row keeps value null.
 */
export const makeDocOptionValue = function (engine, rawId) {
    if (rawId == null || String(rawId).trim() === "") {
        return null;
    }
    return engine + DOC_VALUE_SEPARATOR + String(rawId).trim();
};

/** "papi:abc" -> { engine: "papi", rawId: "abc" }. Unprefixed or blank -> nulls. */
export const parseDocOptionValue = function (value) {
    if (value == null || String(value).trim() === "") {
        return { engine: null, rawId: null };
    }
    var s = String(value);
    var i = s.indexOf(DOC_VALUE_SEPARATOR);
    if (i <= 0) {
        return { engine: null, rawId: null };
    }
    return { engine: s.slice(0, i), rawId: s.slice(i + 1) };
};

export const isPapiSelection = function (value) {
    return parseDocOptionValue(value).engine === DOC_ENGINE_PAPI;
};

/**
 * One row per PAPI docType that WMS lets a customer select.
 * Adding a docType later = adding a row here. It must not require a new
 * filter function, a new loader, or a second catalog HTTP call.
 */
export const PAPI_DOC_TYPES = {
    packingSlip: {
        docType: PAPI_PACKING_SLIP_DOC_TYPE,
        right: "UxPrototypingDirectivesUsePapiPackingSlips",
        templateIdKey: "SmallParcelPapiPackSlipTemplateId",
        templateVersionKey: "SmallParcelPapiPackSlipTemplateVersion",
        formatKey: "SmallParcelPapiPackSlipFormat",
        defaultFormat: "pdf",
        emptyOptionLabel: PAPI_DOCUMENT_NOT_INCLUDED_LABEL,
        optionsStateKey: "optionsPackingReports",
        captionI18n: "Label_Papi_Documents_PackingSlips",
        formatControlId: "smallParcelPapiPackSlipFormat",
        typeControlId: "smallParcelPackSlip",
        typeSelector: "SmallParcelPackSlip",
        scopeKey: "SmallParcelPapiPackSlipScope",
        scopeControlId: "smallParcelPapiPackSlipScope",
        customerSettingKey: "SmallParcelUseNewPackingSlipFeatures",
    },
    ucc128: {
        docType: PAPI_UCC128_DOC_TYPE,
        right: "UxPrototypingDirectivesUsePapiUcc128",
        customerSettingKey: "SmallParcelUseNewUcc128Features",
        templateIdKey: "SmallParcelPapiUcc128TemplateId",
        templateVersionKey: "SmallParcelPapiUcc128TemplateVersion",
        formatKey: "SmallParcelPapiUcc128Format",
        defaultFormat: "zpl",
        emptyOptionLabel: PAPI_DOCUMENT_NOT_INCLUDED_LABEL,
        optionsStateKey: "optionsUcc128Templates",
        captionI18n: "Label_Ucc128_Documents_Caption",
        captionTooltipI18n: "Tooltip_Ucc128_Caption",
        formatControlId: "smallParcelPapiUcc128Format",
        typeControlId: "smallParcelPapiUcc128Template",
        typeSelector: "SmallParcelPapiUcc128TemplateId",
    },
};

/**
 * True when this TPL has the UX directive for the docType.
 * SmallParcelUseNewPackingSlipFeatures is the packing-slip opt-in only
 * (see docs/specs/DOCUMENT_FEATURE_TOGGLE_CHANGE.md). It does not choose the engine for other doc types.
 */
export const isPapiDocEnabled = function (rights, docTypeKey) {
    var d = PAPI_DOC_TYPES[docTypeKey];
    if (!rights || !d) {
        return false;
    }
    return rights.hasOwnProperty(d.right);
};

/** Map MIME strings and short names to pdf|zpl|csv|excel|word|xml. Blank stays blank. */
export const normalizeDocumentFormat = function (format) {
    var f = String(format || "").trim().toLowerCase();
    if (!f) {
        return "";
    }
    if (f === "pdf" || f.indexOf("pdf") !== -1) {
        return "pdf";
    }
    if (f === "zpl" || f === "text/plain") {
        return "zpl";
    }
    if (f === "csv" || f.indexOf("csv") !== -1) {
        return "csv";
    }
    if (f === "excel" || f.indexOf("excel") !== -1) {
        return "excel";
    }
    if (f === "word" || f.indexOf("msword") !== -1) {
        return "word";
    }
    if (f === "xml" || f.indexOf("xml") !== -1) {
        return "xml";
    }
    return f;
};

/** Blank format = no filtering (caller has no Format control). */
export const engineSupportsFormat = function (capabilities, format) {
    var n = normalizeDocumentFormat(format);
    if (!n) {
        return true;
    }
    return (capabilities || []).indexOf(n) !== -1;
};

export const toSsrsOutputType = function (format) {
    var n = normalizeDocumentFormat(format);
    if (SSRS_OUTPUT_TYPES[n]) {
        return SSRS_OUTPUT_TYPES[n];
    }
    return format;
};

/** 4 in in mm — ZPL stock short-side cap (portrait or landscape). */
export const ZPL_STOCK_MAX_SHORT_SIDE_MM = 101.6;
/** 6 in in mm — ZPL stock long-side cap (portrait or landscape). */
export const ZPL_STOCK_MAX_LONG_SIDE_MM = 152.4;
export const ZPL_STOCK_SIZE_EPSILON_MM = 0.5;

/**
 * True only when SmallParcelUseNewUcc128Features is present and exactly true.
 * Absent or any other value is off. Checks the object, customer.options, and Packaging.
 */
export const readSmallParcelUseNewUcc128Features = function (source) {
    if (!source || typeof source !== "object") {
        return false;
    }
    var candidates = [
        source,
        source.options,
        source.Options,
        source.Packaging,
        source.packaging,
    ];
    if (source.Options) {
        candidates.push(source.Options.Packaging, source.Options.packaging);
    }
    if (source.options) {
        candidates.push(source.options.Packaging, source.options.packaging);
    }
    for (var i = 0; i < candidates.length; i++) {
        var bag = candidates[i];
        if (!bag || typeof bag !== "object") {
            continue;
        }
        if (Object.prototype.hasOwnProperty.call(bag, "SmallParcelUseNewUcc128Features")) {
            return bag.SmallParcelUseNewUcc128Features === true;
        }
        if (Object.prototype.hasOwnProperty.call(bag, "smallParcelUseNewUcc128Features")) {
            return bag.smallParcelUseNewUcc128Features === true;
        }
    }
    return false;
};

/**
 * True when the UCC-128 directive is on and every selected customer has
 * SmallParcelUseNewUcc128Features on.
 */
export const selectionUsesNewUcc128Features = function (customerIds, getCustomer, rights) {
    if (!isPapiDocEnabled(rights, "ucc128")) {
        return false;
    }
    if (!customerIds || !customerIds.length || typeof getCustomer !== "function") {
        return false;
    }
    for (var i = 0; i < customerIds.length; i++) {
        if (!readSmallParcelUseNewUcc128Features(getCustomer(customerIds[i]))) {
            return false;
        }
    }
    return true;
};

/** Unique CustomerId values of the rows selected in a LocalKendoGrid-style grid. */
export const getSelectedGridCustomerIds = function (grid) {
    var customerIds = [];
    if (!grid || typeof grid.getKeys !== "function") {
        return customerIds;
    }
    grid.getKeys().forEach(function (key) {
        var item = grid.getItem(key);
        if (item && item.CustomerId != null && customerIds.indexOf(item.CustomerId) === -1) {
            customerIds.push(item.CustomerId);
        }
    });
    return customerIds;
};

/** selectionUsesNewUcc128Features for the customers of the rows selected in a grid. */
export const gridSelectionUsesNewUcc128Features = function (grid, getCustomer, rights) {
    return selectionUsesNewUcc128Features(getSelectedGridCustomerIds(grid), getCustomer, rights);
};

/** Descriptor keys whose UX directive is on for this TPL. Order follows PAPI_DOC_TYPES. */
export const getEnabledPapiDocTypes = function (rights) {
    return Object.keys(PAPI_DOC_TYPES).filter(function (key) {
        return isPapiDocEnabled(rights, key);
    });
};

/**
 * Directive-on doc types that have a customer checkbox.
 * Order follows PAPI_DOC_TYPES: packing slips, then UCC128.
 */
export const getCustomerSettingDocTypes = function (rights) {
    return getEnabledPapiDocTypes(rights).filter(function (key) {
        return PAPI_DOC_TYPES[key].customerSettingKey;
    });
};

// Format options are always pdf and zpl. That is independent of template.engine
// (D4 / OQ-1). Choosing ZPL does hide letter-size stock from the Type list
// (templateFitsZplStock) — format is not independent of which templates are offered.
export const getFormatOptions = function (upperCase) {
    return [
        { Name: upperCase ? "PDF" : "Pdf", Value: "pdf" },
        { Name: upperCase ? "ZPL" : "Zpl", Value: "zpl" },
    ];
};

export const resolvePapiDocumentFormat = function (settings, docTypeKey) {
    var descriptor = PAPI_DOC_TYPES[docTypeKey];
    if (!descriptor) {
        return "pdf";
    }
    var raw = settings && (settings[descriptor.formatKey] || settings[
        descriptor.formatKey.charAt(0).toLowerCase() + descriptor.formatKey.slice(1)
    ]);
    var n = raw && String(raw).trim().toLowerCase();
    if (n === "pdf" || n === "zpl") {
        return n;
    }
    return descriptor.defaultFormat;
};

export const templateFitsZplStock = function (template) {
    var mm = template && template.labelSizeMm;
    var w = mm && Number(mm.widthMm);
    var h = mm && Number(mm.heightMm);
    if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) {
        return false;
    }
    var min = Math.min(w, h);
    var max = Math.max(w, h);
    return min <= ZPL_STOCK_MAX_SHORT_SIDE_MM + ZPL_STOCK_SIZE_EPSILON_MM
        && max <= ZPL_STOCK_MAX_LONG_SIDE_MM + ZPL_STOCK_SIZE_EPSILON_MM;
};

export const filterTemplatesForFormat = function (templates, format) {
    if (String(format || "").toLowerCase() !== "zpl") {
        return templates || [];
    }
    return (templates || []).filter(templateFitsZplStock);
};

var FORMAT_OPTION_LABELS = {
    pdf: "PDF",
    zpl: "ZPL",
    csv: "CSV",
    excel: "Excel",
    word: "Word",
    xml: "XML",
};

/**
 * Format choices for the selected template.
 * "Not Included" (blank) returns PDF and ZPL so the disabled Format control
 * still has a value. A PAPI template offers ZPL only when its stock fits.
 *
 * @param {string|null} selectedValue prefixed value ("papi:abc")
 * @param {Array} rawTemplates this.state.papiDocumentTemplatesRaw
 * @param {Object} descriptor PAPI_DOC_TYPES[key]
 * @returns {Array<{Name:string, Value:string}>}
 */
export const getFormatOptionsForTemplate = function (selectedValue, rawTemplates, descriptor) {
    if (selectedValue == null || String(selectedValue).trim() === "") {
        return getFormatOptions(true);
    }

    var parsed = parseDocOptionValue(selectedValue);

    if (parsed.engine === DOC_ENGINE_PAPI) {
        var rawTemplate = (rawTemplates || []).find(function (template) {
            return template && (template.id === parsed.rawId);
        });
        if (templateFitsZplStock(rawTemplate)) {
            return [
                { Name: "PDF", Value: "pdf" },
                { Name: "ZPL", Value: "zpl" },
            ];
        }
        return [{ Name: "PDF", Value: "pdf" }];
    }

    return getFormatOptions(true);
};

/**
 * Translated optgroup suffixes. translate is window.translate. A missing key stays English.
 */
export const formatSuffixLabelsFromTranslate = function (translate) {
    function text(key, fallback) {
        if (typeof translate !== "function") {
            return fallback;
        }
        var value = translate(key);
        if (!value || value === key) {
            return fallback;
        }
        return value;
    }
    return {
        zplOrPdf: " - " + text("Label_Papi_FormatSuffix_ZplOrPdf", "ZPL or PDF"),
        pdfOnly: " - " + text("Label_Papi_FormatSuffix_PdfOnly", "PDF Only"),
    };
};

/**
 * Suffix for a Template optgroup. ZPL is offered only when some template in the
 * group fits ZPL stock.
 *
 * @param {Array} templates raw templates in a PAPI size group
 * @param {object} [labels] from formatSuffixLabelsFromTranslate
 * @returns {string}
 */
export const optGroupFormatSuffix = function (templates, labels) {
    labels = labels || formatSuffixLabelsFromTranslate();
    var zplOrPdf = labels.zplOrPdf || " - ZPL or PDF";
    var pdfOnly = labels.pdfOnly || " - PDF Only";
    var anyZpl = (templates || []).some(templateFitsZplStock);
    return anyZpl ? zplOrPdf : pdfOnly;
};

/** Trimmed string, or null when absent/blank (TPL-wide / unbound). */
export const normalizePapiTemplateCustomerId = function (customerId) {
    if (customerId == null) return null;
    var s = String(customerId).trim();
    return s ? s : null;
};

/**
 * Distinct WMS customer ids as strings. `"1"` and `1` collapse to one.
 * Empty input → [].
 */
export const distinctWmsCustomerIds = function (wmsCustomerIds) {
    var raw = wmsCustomerIds == null
        ? []
        : (Array.isArray(wmsCustomerIds) ? wmsCustomerIds : [wmsCustomerIds]);
    var seen = {};
    var out = [];
    raw.forEach(function (id) {
        var n = normalizePapiTemplateCustomerId(id);
        if (!n || seen[n]) return;
        seen[n] = true;
        out.push(n);
    });
    return out;
};

/**
 * True when `customerIds` is present (string[]). Empty array counts as present.
 * Omitted or null = use scalar `customerId` instead.
 */
export const papiTemplateHasCustomerIds = function (template) {
    return !!(template && template.customerIds != null);
};

/**
 * Bound WMS customer ids.
 * customerIds: string[] — use it and ignore customerId (dual-write first id).
 * customerId: string — used only when customerIds is omitted.
 */
export const extractPapiTemplateBoundCustomerIds = function (template) {
    if (!template) return [];
    if (papiTemplateHasCustomerIds(template)) {
        return distinctWmsCustomerIds(template.customerIds);
    }
    var n = normalizePapiTemplateCustomerId(template.customerId);
    return n ? [n] : [];
};

/**
 * Both keys omitted = TPL-wide. customerIds omitted = scalar customerId string.
 * customerIds present = any listed string; empty/blank list = hidden.
 * One WMS customer = unbound plus bound templates that include that customer.
 * Multiple WMS customers = unbound plus bound templates every selected customer can use.
 */
export const isPapiTemplateVisibleForCustomers = function (template, wmsCustomerIds) {
    var bound = extractPapiTemplateBoundCustomerIds(template);
    var ids = distinctWmsCustomerIds(wmsCustomerIds);
    // Empty customerIds[] = fail closed; omitted/blank customerId = TPL-wide.
    if (!bound.length) return !papiTemplateHasCustomerIds(template);
    if (!ids.length) {
        return false;
    }
    if (ids.length === 1) {
        return bound.indexOf(ids[0]) !== -1;
    }
    return ids.every(function (wmsId) {
        return bound.indexOf(wmsId) !== -1;
    });
};

/**
 * @param {Array} documentTemplates - raw payload.documentTemplates
 * @param {string} docType - exact, ordinal (PAPI_DOC_TYPES[x].docType)
 * @param {boolean} includeDraft
 * @param {number|string|Array} [wmsCustomerIds] - omit/empty → unbound only (fail closed)
 * Visibility: both keys omitted = TPL-wide; customerIds omitted = customerId string;
 * customerIds: string[] = any listed id for one WMS customer; all must match when several.
 */
export const filterPapiDocumentTemplates = function (documentTemplates, docType, includeDraft, wmsCustomerIds) {
    if (!documentTemplates || !documentTemplates.length || !docType) {
        return [];
    }
    // A template's `engine` describes how it was authored, not which output
    // format (PDF/ZPL) it can be rendered as at print time — PAPI supports
    // rendering any packingSlip template as either format. Do not filter by engine here.
    // Unbound templates are TPL-wide. Bound templates must include every selected WMS customer.
    return documentTemplates.filter(function (template) {
        if (!template || template.docType !== docType) {
            return false;
        }
        if (!includeDraft && template.status && template.status.toLowerCase() !== "published") {
            return false;
        }
        return isPapiTemplateVisibleForCustomers(template, wmsCustomerIds);
    });
};

/** Trimmed labelSizeLabel, or Other when absent/blank. */
export const normalizePapiTemplateLabelSizeLabel = function (labelSizeLabel) {
    if (labelSizeLabel == null) return PAPI_PACKING_SLIP_OTHER_SIZE_LABEL;
    var s = String(labelSizeLabel).trim();
    return s ? s : PAPI_PACKING_SLIP_OTHER_SIZE_LABEL;
};

var LABEL_SIZE_ORIENTATION_RANK = { portrait: 0, landscape: 1 };

/**
 * Split "US Letter (landscape)" into base "US Letter" plus orientation.
 * Labels that do not end in (portrait) or (landscape) stay one group.
 */
export const splitLabelSizeOrientation = function (labelSizeLabel) {
    var label = normalizePapiTemplateLabelSizeLabel(labelSizeLabel);
    var match = /^(.*?)\s*\((portrait|landscape)\)\s*$/i.exec(label);
    if (!match || !match[1]) {
        return { base: label, orientation: "", rank: 2 };
    }
    return {
        base: match[1].replace(/\s+$/, ""),
        orientation: match[2].toLowerCase(),
        rank: LABEL_SIZE_ORIENTATION_RANK[match[2].toLowerCase()],
    };
};

/**
 * Walk mapper output so id lookups ignore optgroup wrappers.
 * Ungrouped rows (Not Included) stay in order.
 */
export const flattenPapiDropdownOptions = function (options) {
    var out = [];
    (options || []).forEach(function (option) {
        if (option && option.isOptGroup && option.options) {
            option.options.forEach(function (child) {
                out.push(child);
            });
        } else if (option) {
            out.push(option);
        }
    });
    return out;
};

/**
 * @param {Array} documentTemplates - published packingSlip templates
 * @param {string|false} [notIncludedLabel] - Leading empty option label.
 *   Pass {@link PAPI_PACKING_SLIP_NOT_INCLUDED_LABEL} for Settings / Pack & Ship.
 *   Pass `false` to omit the empty row (Documents → Packing Slips — Generate always needs a template).
 *   Omit / pass `undefined` / `""` for a blank Name (legacy).
 * @param {boolean} [appendFormatSuffix] - When true, append " - ZPL or PDF" or
 *   " - PDF Only" to each size group. Settings passes true. Other callers leave
 *   it off so Pack & Ship group labels stay unchanged.
 * Groups by trimmed labelSizeLabel (blank → Other). Family order is first-seen
 * base size (orientation stripped). Portrait and landscape of that base sit
 * next to each other, portrait first. Names inside a group keep catalog order.
 */
export const mapPapiTemplatesToDropdownOptions = function (documentTemplates, notIncludedLabel, appendFormatSuffix, formatSuffixLabels) {
    const options = [];
    if (notIncludedLabel !== false) {
        options.push({
            Name: notIncludedLabel == null ? "" : notIncludedLabel,
            id: null,
            version: null,
            value: null,
        });
    }
    const groups = [];
    const groupByLabel = {};
    (documentTemplates || []).forEach(function (template) {
        var groupLabel = normalizePapiTemplateLabelSizeLabel(template.labelSizeLabel);
        var group = groupByLabel[groupLabel];
        if (!group) {
            group = {
                isOptGroup: true,
                Name: groupLabel,
                id: null,
                options: [],
                _rawTemplates: [],
            };
            groupByLabel[groupLabel] = group;
            groups.push(group);
        }
        group._rawTemplates.push(template);
        group.options.push({
            Name: template.name,
            id: template.id,
            version: template.version,
            engine: template.engine,
            value: makeDocOptionValue(DOC_ENGINE_PAPI, template.id),
        });
    });
    var familyOrder = {};
    var familySeq = 0;
    groups.forEach(function (group) {
        var parts = splitLabelSizeOrientation(group.Name);
        group._base = parts.base;
        group._orientRank = parts.rank;
        if (familyOrder[parts.base] == null) {
            familyOrder[parts.base] = familySeq;
            familySeq += 1;
        }
        group._familyOrder = familyOrder[parts.base];
    });
    groups.sort(function (a, b) {
        if (a._familyOrder !== b._familyOrder) {
            return a._familyOrder - b._familyOrder;
        }
        return a._orientRank - b._orientRank;
    });
    groups.forEach(function (group) {
        if (group.options.length) {
            delete group._base;
            delete group._orientRank;
            delete group._familyOrder;
            if (appendFormatSuffix) {
                group.Name = group.Name + optGroupFormatSuffix(
                    group._rawTemplates,
                    formatSuffixLabels
                );
            }
            delete group._rawTemplates;
            options.push(group);
        }
    });
    return options;
};

export const findPapiTemplateVersion = function (options, templateId) {
    if (!templateId || !options) {
        return null;
    }
    var parsed = parseDocOptionValue(templateId);
    var rawId = parsed.rawId || templateId;
    const match = flattenPapiDropdownOptions(options).find(function (option) {
        return option.id === rawId
            || option.value === templateId
            || option.value === makeDocOptionValue(DOC_ENGINE_PAPI, rawId);
    });
    return match ? match.version : null;
};

/**
 * The Type option list for one docType. PAPI rows only.
 * opts = { docTypeKey, papiTemplates, format, wmsCustomerIds, includeDraft,
 *          includeEmptyRow, filterByFormat, includeFormatSuffix }
 * filterByFormat defaults to true so Pack & Ship and the packing-slip modal
 * keep hiding templates that cannot render the chosen format.
 * Settings passes filterByFormat: false and includeFormatSuffix: true.
 */
export const buildDocumentTypeOptions = function (opts) {
    opts = opts || {};
    var descriptor = PAPI_DOC_TYPES[opts.docTypeKey];
    if (!descriptor) {
        return [];
    }
    var options = [];
    var filterByFormat = opts.filterByFormat !== false;
    var appendFormatSuffix = opts.includeFormatSuffix === true;

    if (opts.includeEmptyRow !== false) {
        options.push({ Name: descriptor.emptyOptionLabel, value: null, id: null, version: null });
    }

    if (!filterByFormat || engineSupportsFormat(PAPI_FORMAT_CAPABILITIES, opts.format)) {
        var papi = filterPapiDocumentTemplates(
            opts.papiTemplates, descriptor.docType, opts.includeDraft, opts.wmsCustomerIds);
        if (filterByFormat) {
            papi = filterTemplatesForFormat(papi, opts.format);
        }
        options = options.concat(mapPapiTemplatesToDropdownOptions(
            papi, false, appendFormatSuffix, opts.formatSuffixLabels));
    }

    return options;
};

/**
 * Resolve which option should be selected.
 * savedValue is a prefixed PAPI value ("papi:…") or empty.
 * Returns { value, templateId, templateVersion }.
 */
export const resolveTemplateSelection = function (options, savedValue, autoSelectFirst) {
    var list = flattenPapiDropdownOptions(options);
    var saved = savedValue && list.find(function (option) {
        return option.value === savedValue;
    });
    var chosen = saved || null;
    if (!chosen && autoSelectFirst) {
        chosen = list.find(function (option) {
            return option && option.value;
        }) || null;
    }
    return {
        value: chosen ? chosen.value : null,
        templateId: chosen ? (chosen.id || null) : null,
        templateVersion: chosen ? (chosen.version != null ? chosen.version : null) : null,
    };
};

/** Prefixed PAPI value when a template id is saved. Legacy keys are not read. */
export const resolveSavedDocumentValue = function (packaging, docTypeKey) {
    var descriptor = PAPI_DOC_TYPES[docTypeKey];
    if (!descriptor || !packaging) return null;

    var papiId = packaging[descriptor.templateIdKey];
    if (papiId && String(papiId).trim()) {
        return makeDocOptionValue(DOC_ENGINE_PAPI, papiId);
    }
    return null;
};

/**
 * Schema load: a saved PAPI id that is no longer in the catalog.
 * Clears only the PAPI keys. Legacy runner / address / carton stay until the user changes Type.
 */
export const clearUnavailablePapiTemplate = function (parentObj, docTypeKey) {
    var d = PAPI_DOC_TYPES[docTypeKey];
    if (!d || !parentObj) {
        return parentObj;
    }
    parentObj[d.templateIdKey] = "";
    parentObj[d.templateVersionKey] = null;
    return parentObj;
};

/**
 * Persist a PAPI template from a prefixed Type value. Does not write the prefix.
 * Not Included clears the PAPI id only. Legacy runner, address, and carton name stay.
 */
export const applyDocumentTypeSelection = function (parentObj, docTypeKey, selectedValue, typeOptions) {
    var d = PAPI_DOC_TYPES[docTypeKey];
    if (!d || !parentObj) {
        return parentObj;
    }
    var parsed = parseDocOptionValue(selectedValue);
    if (parsed.engine === DOC_ENGINE_PAPI) {
        parentObj[d.templateIdKey] = parsed.rawId;
        parentObj[d.templateVersionKey] = findPapiTemplateVersion(typeOptions, parsed.rawId);
    } else {
        parentObj[d.templateIdKey] = "";
        parentObj[d.templateVersionKey] = null;
    }
    return parentObj;
};

/**
 * Prefixed PAPI value when every packaging has the same non-blank saved template id; else null.
 * Legacy ids are ignored (mixed selections never show Legacy).
 */
export const resolveSharedPapiDocumentValue = function (packagingList, docTypeKey) {
    var descriptor = PAPI_DOC_TYPES[docTypeKey];
    var list = packagingList || [];
    if (!descriptor || !list.length) return null;
    var first = null;
    for (var i = 0; i < list.length; i++) {
        var raw = list[i] && list[i][descriptor.templateIdKey];
        var id = raw == null ? "" : String(raw).trim();
        if (!id) return null;
        if (first === null) first = id;
        else if (id !== first) return null;
    }
    return makeDocOptionValue(DOC_ENGINE_PAPI, first);
};

/** Same value on every packaging (trimmed, case-insensitive), else "". */
export const resolveSharedPackagingValue = function (packagingList, key) {
    var camel = key.charAt(0).toLowerCase() + key.slice(1);
    var first = null;
    var list = packagingList || [];
    for (var i = 0; i < list.length; i++) {
        var p = list[i] || {};
        var v = String(p[key] || p[camel] || "").trim().toLowerCase();
        if (first === null) first = v;
        else if (v !== first) return "";
    }
    return first || "";
};

/** New document UI flag on a GlobalService customer row. Boolean true only. */
export const customerRowUsesNewPackingSlipFeatures = function (customerData) {
    return isCustomerUsingNewPackingSlipFeatures(customerData);
};

/**
 * Mixed-customer Documents → Packing Slips is allowed only on the PAPI path:
 * directive on and every selected customer on the new packing slip UI.
 */
export const canUsePapiPackingSlipsForMixedCustomers = function (rights, customerRows) {
    if (!isPapiDocEnabled(rights, "packingSlip")) return false;
    var rows = customerRows || [];
    return rows.length > 1 && rows.every(customerRowUsesNewPackingSlipFeatures);
};

/**
 * Directive on, two or more resolved customer rows, and some but not all
 * have Use New Packing Slip Features. Unresolved rows never count as a disagreement.
 */
export const customerRowsDisagreeOnNewPackingSlipFeatures = function (rights, customerRows) {
    if (!isPapiDocEnabled(rights, "packingSlip")) return false;
    var rows = customerRows || [];
    if (rows.length < 2 || !rows.every(function (r) { return !!r; })) return false;
    var onCount = rows.filter(customerRowUsesNewPackingSlipFeatures).length;
    return onCount > 0 && onCount < rows.length;
};

/** Saved PAPI packing-slip template id from a GlobalService customer row. */
export const getCustomerPapiPackSlipTemplateId = function (customerData) {
    var options = customerData && customerData.options;
    if (!options) {
        return null;
    }
    var packaging = options.Packaging || options.packaging;
    return (packaging && (packaging.SmallParcelPapiPackSlipTemplateId || packaging.smallParcelPapiPackSlipTemplateId))
        || options.SmallParcelPapiPackSlipTemplateId
        || options.smallParcelPapiPackSlipTemplateId
        || null;
};
export const isPapiPackingSlipsOptedIn = function (templateId) {
    return !!(templateId && String(templateId).trim());
};

/**
 * Customer opt-in for the new packing slip UI. Missing or false stays on legacy (TW-40863 D1).
 * Accepts a GlobalService customer row, an options object, or Packaging.
 */
export const isCustomerUsingNewPackingSlipFeatures = function (source) {
    if (!source || source === true) {
        return source === true;
    }
    if (source.SmallParcelUseNewPackingSlipFeatures === true
        || source.smallParcelUseNewPackingSlipFeatures === true) {
        return true;
    }
    var customerOptions = source.options || source.Options;
    if (customerOptions && customerOptions !== source) {
        return isCustomerUsingNewPackingSlipFeatures(customerOptions);
    }
    var packaging = source.Packaging || source.packaging;
    if (packaging && packaging !== source) {
        return isCustomerUsingNewPackingSlipFeatures(packaging);
    }
    return false;
};

// Strict boolean true (same as isCustomerUsingNewPackingSlipFeatures); string "true" is off.
export const usesNewPackingSlipUi = function (rights, customerLike) {
    if (!isPapiDocEnabled(rights, "packingSlip")) {
        return false;
    }
    return isCustomerUsingNewPackingSlipFeatures(customerLike);
};

/** Read {templateId, templateVersion} for one docType from customer Packaging. */
export const readCustomerPapiTemplateSetting = function (packaging, docTypeKey) {
    var descriptor = PAPI_DOC_TYPES[docTypeKey];
    if (!packaging || !descriptor) {
        return { templateId: null, templateVersion: null };
    }
    return {
        templateId: packaging[descriptor.templateIdKey] || null,
        templateVersion: packaging[descriptor.templateVersionKey] ?? null,
    };
};

/** @deprecated Packing-slip shape (adds slip-only `scope`). */
export const readCustomerPapiPackSlipSettings = function (packaging) {
    var base = readCustomerPapiTemplateSetting(packaging, "packingSlip");
    return {
        templateId: base.templateId,
        templateVersion: base.templateVersion,
        scope: (packaging && (packaging.SmallParcelPapiPackSlipScope || packaging.smallParcelPapiPackSlipScope)) || "",
        format: (packaging && (packaging.SmallParcelPapiPackSlipFormat || packaging.smallParcelPapiPackSlipFormat)) || "",
    };
};

/** True when customer packing-slip layout is Per Package (PAPI scope "package"). Default/empty = Per Order. */
export const isPapiPackageSlipScope = function (scopeOrCustomerOptions) {
    var scope = scopeOrCustomerOptions;
    if (scopeOrCustomerOptions && typeof scopeOrCustomerOptions === "object") {
        scope = scopeOrCustomerOptions.smallParcelPapiPackSlipScope
            || scopeOrCustomerOptions.SmallParcelPapiPackSlipScope
            || "";
    }
    return String(scope || "").toLowerCase() === "package";
};

/** Documents menu: gate only for Per Package when any selected order is not FullyPackageAllocated. */
export const shouldDisablePapiPackingSlipsForSelection = function (isPapiEngine, isPackageScope, anyNotFullyPackageAllocated) {
    return !!(isPapiEngine && isPackageScope && anyNotFullyPackageAllocated);
};

/** UCC-128 generate is always per-package: disable when the order is not FullyPackageAllocated. */
export const shouldDisablePapiUcc128ForSelection = function (notFullyPackageAllocated) {
    return !!notFullyPackageAllocated;
};

/** Decode ZPL bytes from axios arraybuffer response for printer dispatch. */
export const decodeZplResponseData = function (responseData) {
    if (responseData == null || typeof responseData === "string") {
        return responseData;
    }

    var buffer = null;
    if (responseData instanceof ArrayBuffer) {
        buffer = responseData;
    } else if (responseData.buffer instanceof ArrayBuffer) {
        buffer = responseData.buffer;
    } else if (Object.prototype.toString.call(responseData) === "[object ArrayBuffer]") {
        buffer = responseData;
    }

    if (!buffer) {
        return responseData;
    }

    if (typeof TextDecoder !== "undefined") {
        return new TextDecoder("utf-8").decode(buffer);
    }
    var bytes = new Uint8Array(buffer);
    var result = "";
    for (var i = 0; i < bytes.length; i++) {
        result += String.fromCharCode(bytes[i]);
    }
    return result;
};

/**
 * Safely extract documentTemplates[] from a raw getCarrierAccountSchema() response value.
 * Handles the case where the API returns either a parsed object or a JSON string.
 */
export const parseDocumentTemplatesFromSchemaResponse = function (schemaData) {
    let schema = schemaData;
    if (typeof schemaData === "string") {
        try {
            schema = JSON.parse(schemaData);
        } catch (_) {
            return null;
        }
    }
    // null means the payload shape is unknown. Callers must not treat that as
    // "every template is gone" — that cleared saved selections on the next Save.
    if (!schema || typeof schema !== "object" || !schema.payload
        || !Array.isArray(schema.payload.documentTemplates)) {
        return null;
    }
    return schema.payload.documentTemplates;
};

var parsePapiReprintJsonObject = function (data) {
    if (data == null) {
        return null;
    }
    var obj = data;
    if (typeof obj === "string") {
        try {
            obj = JSON.parse(obj);
        } catch (_) {
            return null;
        }
    }
    if (typeof obj !== "object" || Array.isArray(obj)) {
        return null;
    }
    return obj;
};

var parsePapiReprintSlipWarning = function (obj) {
    if (!obj) {
        return null;
    }
    var slipWarning = obj.slipWarning != null ? obj.slipWarning : obj.SlipWarning;
    if (slipWarning == null) {
        return null;
    }
    slipWarning = String(slipWarning).trim();
    return slipWarning || null;
};

/**
 * JsonResult `pdf` may be base64 text or a byte[] (number[]) from the API.
 */
var papiReprintPdfFieldToBlob = function (pdfField) {
    if (pdfField == null) {
        return null;
    }
    if (pdfField instanceof Blob) {
        if (pdfField.type) {
            return pdfField;
        }
        return new Blob([pdfField], { type: "application/pdf" });
    }
    if (pdfField instanceof ArrayBuffer) {
        return new Blob([pdfField], { type: "application/pdf" });
    }
    if (ArrayBuffer.isView(pdfField)) {
        return new Blob([pdfField], { type: "application/pdf" });
    }
    if (Array.isArray(pdfField)) {
        if (!pdfField.length) {
            return null;
        }
        var bytes = new Uint8Array(pdfField.length);
        for (var i = 0; i < pdfField.length; i++) {
            bytes[i] = pdfField[i] & 0xff;
        }
        return new Blob([bytes], { type: "application/pdf" });
    }
    if (typeof pdfField === "string") {
        var base64 = pdfField.trim();
        if (!base64) {
            return null;
        }
        var dataUriMatch = base64.match(/^data:[^;]+;base64,(.+)$/i);
        if (dataUriMatch) {
            base64 = dataUriMatch[1];
        }
        base64 = base64.replace(/\s/g, "");
        try {
            var binary = atob(base64);
            var out = new Uint8Array(binary.length);
            for (var j = 0; j < binary.length; j++) {
                out[j] = binary.charCodeAt(j);
            }
            return new Blob([out], { type: "application/pdf" });
        } catch (_) {
            return null;
        }
    }
    return null;
};

/**
 * ParcelReprintWithPackingSlipsPdfContent JsonResult: { pdf, slipWarning } (PascalCase ok).
 * Returns null when pdf is missing or cannot be converted to a blob.
 */
export const parsePapiReprintPdfJsonResponse = function (data) {
    var obj = parsePapiReprintJsonObject(data);
    if (!obj) {
        return null;
    }
    var pdfField = obj.pdf != null ? obj.pdf : obj.Pdf;
    var pdfBlob = papiReprintPdfFieldToBlob(pdfField);
    if (!pdfBlob) {
        return null;
    }
    return { pdfBlob: pdfBlob, slipWarning: parsePapiReprintSlipWarning(obj) };
};

var readZplLabelList = function (value) {
    if (value == null) {
        return null;
    }
    var raw = Array.isArray(value) ? value : [value];
    var labels = [];
    raw.forEach(function (item) {
        if (item == null) {
            return;
        }
        var text = String(item);
        if (text.trim()) {
            labels.push(text);
        }
    });
    return labels.length ? labels : null;
};

/**
 * ParcelReprintWithPackingSlipsZplContent JsonResult: { labels: string[], slipWarning }.
 * Also accepts a single zpl / Zpl / ZPL string. Returns null when no labels are present.
 */
export const parsePapiReprintZplJsonResponse = function (data) {
    var obj = parsePapiReprintJsonObject(data);
    if (!obj) {
        return null;
    }
    var labels = readZplLabelList(obj.labels != null ? obj.labels : obj.Labels);
    if (!labels) {
        labels = readZplLabelList(obj.zpl != null ? obj.zpl : (obj.Zpl != null ? obj.Zpl : obj.ZPL));
    }
    if (!labels) {
        return null;
    }
    return { labels: labels, slipWarning: parsePapiReprintSlipWarning(obj) };
};

/**
 * Decode a WMS ErrorResponse out of an axios error whose request used
 * responseType:'arraybuffer'. Returns the first meaningful descriptor string,
 * or null when the body cannot be parsed.
 */
export const extractPapiErrorMessage = function (error) {
    try {
        const buffer = error && error.response && error.response.data;
        if (!buffer) return null;
        const text = typeof buffer === "string"
            ? buffer
            : decodeZplResponseData(buffer);
        if (!text || typeof text !== "string") return null;
        const parsed = JSON.parse(text);
        const descriptions = parsed && parsed.Descriptions;
        if (!descriptions || !descriptions.length) return null;
        const first = descriptions[0];
        if (first.Hint) return first.Hint;
        if (first.Descriptors && first.Descriptors.length) return first.Descriptors[0];
        return null;
    } catch (_) {
        return null;
    }
};

/**
 * Pack and Print Packing Slip gate. PAPI selection needs template id; SSRS
 * selection also needs Address Configuration. Address is never required for PAPI.
 */
export const isPackAndPrintPackingSlipConfigured = function (isIntegratedPackingSlips, packAdnShipObj, selectedValue, useNewPackingSlipUi) {
    if (!isIntegratedPackingSlips || !packAdnShipObj) {
        return false;
    }
    if (useNewPackingSlipUi) {
        if (selectedValue) {
            var parsed = parseDocOptionValue(selectedValue);
            return parsed.engine === DOC_ENGINE_PAPI
                && !!(parsed.rawId && String(parsed.rawId).trim());
        }
        return !!(packAdnShipObj.SmallParcelPapiPackSlipTemplateId
            && String(packAdnShipObj.SmallParcelPapiPackSlipTemplateId).trim());
    }
    return !!(packAdnShipObj.SmallParcelPackSlip && packAdnShipObj.SmallParcelAddressTemplate);
};

/** Payload for FindOrders/PapiPackingSlipReportGenerate (Documents + Pack and Print). */
export const buildPapiPackingSlipGeneratePayload = function (opts) {
    opts = opts || {};
    var payload = {
        TransactionIds: opts.transactionIds,
        CustomerId: opts.customerId,
        PapiTemplateId: opts.templateId,
        PapiTemplateVersion: opts.templateVersion,
        OutputFormat: opts.outputFormat || "pdf",
    };
    if (opts.papiSlipScope != null && String(opts.papiSlipScope).trim() !== "") {
        payload.PapiSlipScope = opts.papiSlipScope;
    }
    var customerIds = distinctWmsCustomerIds(opts.customerIds)
        .map(function (id) { return Number(id); })
        .filter(function (id) { return Number.isFinite(id); });
    if (customerIds.length > 1) {
        payload.CustomerIds = customerIds;
    }
    return payload;
};

/**
 * Fields for showProgressModal after ship. PAPI ships slips via
 * documents.combined — do not signal the SSRS packing-slip fallback.
 * selectionEngine: DOC_ENGINE_PAPI | DOC_ENGINE_LEGACY | null.
 */
export const buildSsrsPackingSlipsProgressEventFields = function (
    selectionEngine,
    isIntegratedPackingSlips,
    packAdnShipObj,
    transactionIds,
    customerId
) {
    if (selectionEngine !== DOC_ENGINE_LEGACY) {
        return { isIntegratedPackingSlips: false, packingSlipsData: null };
    }
    return {
        isIntegratedPackingSlips: !!isIntegratedPackingSlips,
        packingSlipsData: {
            TemplatedRunner: packAdnShipObj && packAdnShipObj.SmallParcelPackSlip,
            TransactionIds: transactionIds,
            CustomerId: customerId,
            TemplateId: packAdnShipObj && packAdnShipObj.SmallParcelAddressTemplate,
            OutputType: "application/pdf",
        },
    };
};

/** Saved Carton Label Template name from customer packaging and/or PackAndShip OrderInfo. */
export const readSavedDefaultCartonLabelName = function (orderInfo, packaging) {
    var candidates = [];
    if (packaging) {
        candidates.push(packaging.SmallParcelDefaultCartonLabel, packaging.smallParcelDefaultCartonLabel);
    }
    if (orderInfo) {
        candidates.push(orderInfo.smallParcelDefaultCartonLabel, orderInfo.SmallParcelDefaultCartonLabel);
    }
    for (var i = 0; i < candidates.length; i++) {
        var raw = candidates[i];
        if (raw == null) {
            continue;
        }
        var trimmed = String(raw).trim();
        if (trimmed && trimmed !== "Not specified") {
            return trimmed;
        }
    }
    return "";
};

/**
 * Match Settings SmallParcelDefaultCartonLabel (raw template Name) to a Pack and Ship
 * carton option. Options use decorated Name (Retailer prefix, Library- stripped); sourceName
 * is the catalog Name from order pack info.
 */
export const resolveLegacyCartonLabelTemplateId = function (options, savedName) {
    var saved = String(savedName || "").trim();
    if (!saved || saved === "Not specified") {
        return 0;
    }
    var list = options || [];
    for (var i = 0; i < list.length; i++) {
        var opt = list[i];
        if (!opt || opt.isSeparator) {
            continue;
        }
        if (opt.LabelTemplateId === "" || opt.LabelTemplateId == null) {
            continue;
        }
        if (isBlankLabelId(opt.LabelTemplateId)) {
            continue;
        }
        var source = opt.sourceName != null
            ? String(opt.sourceName).trim()
            : String(opt.Name || "").trim();
        if (source === saved || String(opt.Name || "").trim() === saved) {
            return opt.LabelTemplateId;
        }
        if (source && saved && source.toLowerCase() === saved.toLowerCase()) {
            return opt.LabelTemplateId;
        }
    }
    return 0;
};

/** Blank carton/transaction template id. A select reports "0"; legacy options use 0. */
export const isBlankLabelId = function (value) {
    return value == null || value === "" || value === 0 || value === "0";
};

/**
 * Legacy template ids are numbers. PAPI template ids are strings. A select always
 * reports a string. Compare through String so both families match.
 */
export const sameLabelId = function (a, b) {
    if (isBlankLabelId(a) && isBlankLabelId(b)) {
        return true;
    }
    return String(a) === String(b);
};

/** Index of the option whose LabelTemplateId matches value, or -1. */
export const indexOfLabelId = function (options, value) {
    var list = options || [];
    for (var i = 0; i < list.length; i++) {
        if (list[i] && sameLabelId(list[i].LabelTemplateId, value)) {
            return i;
        }
    }
    return -1;
};

/**
 * The id to store when options change. The option's own id when value matches
 * one of them, otherwise the blank sentinel 0. Keeps React state on a real option
 * so the dropdown does not show one value while state holds another.
 */
export const reconcileLabelSelection = function (options, value) {
    var index = indexOfLabelId(options, value);
    if (index < 0) {
        return 0;
    }
    return options[index].LabelTemplateId;
};

/**
 * Next non-blank template id after value. A missing current id starts at the
 * first real template. Blank rows such as "Not specified" are skipped, so one
 * scan does not land back on the first empty option.
 */
export const nextLabelId = function (options, value) {
    var list = options || [];
    if (!list.length) {
        return null;
    }
    var nextIndex = indexOfLabelId(list, value);
    for (var step = 0; step < list.length; step++) {
        nextIndex = (nextIndex + 1) % list.length;
        var id = list[nextIndex] && list[nextIndex].LabelTemplateId;
        if (!isBlankLabelId(id)) {
            return id;
        }
    }
    return list[0].LabelTemplateId;
};
